/**
 * REF-1 — the refund component model.
 *
 * A refund is a COMPONENT VECTOR, never a single amount. Where each component posts in
 * the Sales Waterfall is fixed by `WATERFALL_POSTING` below and is the single reason this
 * module exists: giving a tax or a tip back is not lost revenue, and netting it into
 * `Refunds` would understate the revenue the operator actually kept.
 *
 * REF-7 — every report that computes REVENUE must read `itemRefundAmount()` and never
 * `refundTotal()`. That is why the two are named asymmetrically: `refundTotal` is a cash
 * figure (what left the till) and `itemRefundAmount` is a revenue figure (what stopped
 * being a sale). A report that grabs the wrong one silently corrupts CLV, menu
 * engineering and category rankings, so both are exported from here and nowhere else.
 */

/** The eight components a refund can return. */
export type RefundComponentKey =
    | 'item' | 'tax' | 'tip' | 'gratuity' | 'convenienceFee' | 'deliveryFee' | 'serviceCharge' | 'cardProcessingFee';

/** REF-1 — single-valued per order. An order carries at most one refund event. */
export type RefundType = 'full' | 'item' | 'fee';

/** Matches the sales-source toggle the report screens already carry. */
export type RefundSalesSource = 'overall' | 'direct' | 'thirdparty';

export interface RefundComponents {
    item: number;
    tax: number;
    tip: number;
    gratuity: number;
    convenienceFee: number;
    deliveryFee: number;
    serviceCharge: number;
    cardProcessingFee: number;
}

/** Where a component lands in the waterfall, and whether it is a revenue event. */
export type WaterfallPosting = 'refunds' | 'tax' | 'tipsAndGratuity' | 'others';

export interface PostingRule {
    posting: WaterfallPosting;
    /** REF-1 — ONLY the item component reduces Net Sales. */
    reducesNetSales: boolean;
    label: string;
}

/**
 * REF-1 posting table. This is the arithmetic foundation the rest of the epic renders on
 * top of; changing a row here changes every surface at once, which is the point.
 */
export const WATERFALL_POSTING: Record<RefundComponentKey, PostingRule> = {
    item: { posting: 'refunds', reducesNetSales: true, label: 'Items' },
    tax: { posting: 'tax', reducesNetSales: false, label: 'Tax' },
    tip: { posting: 'tipsAndGratuity', reducesNetSales: false, label: 'Tips' },
    gratuity: { posting: 'tipsAndGratuity', reducesNetSales: false, label: 'Gratuity' },
    convenienceFee: { posting: 'others', reducesNetSales: false, label: 'Convenience fee' },
    deliveryFee: { posting: 'others', reducesNetSales: false, label: 'Delivery fee' },
    serviceCharge: { posting: 'others', reducesNetSales: false, label: 'Service charge' },
    cardProcessingFee: { posting: 'others', reducesNetSales: false, label: 'Card processing fee' },
};

export const REFUND_COMPONENT_KEYS: RefundComponentKey[] =
    ['item', 'tax', 'tip', 'gratuity', 'convenienceFee', 'deliveryFee', 'serviceCharge', 'cardProcessingFee'];

export const REFUND_TYPE_LABEL: Record<RefundType, string> = {
    full: 'Full',
    item: 'Item',
    fee: 'Fee',
};

/** REF-6 — the two reason groups. A fee reason is a control signal, not a quality one. */
export type RefundReasonGroup = 'item' | 'fee';

/** REF-5 — one original tender and what was returned to it. */
export interface RefundTenderReturn {
    /** 'Visa ***4821', 'Cash', 'Amex ***1002'. Free text as recorded at payment time. */
    tender: string;
    amount: number;
}

/**
 * REF-4 — the three buckets the EOD Summary Report reports sales in: Cash Sales,
 * Alternative Payments and Credit Sales. A refund returns to its ORIGINATING tender, so
 * each bucket must be net of the refunds that came back to it.
 */
export type TenderClass = 'cash' | 'credit' | 'alternative';

/**
 * Classify a free-text tender name.
 *
 * `alternative` is the DEFAULT, not `credit`: a tender this function does not recognise is
 * far more likely to be a wallet, gift card or platform settlement than a card, and
 * mis-filing one into Credit Sales would corrupt Expected Funds — the one column an
 * operator reconciles against their bank.
 */
export function tenderClassOf(tender: string): TenderClass {
    const t = String(tender ?? '').toLowerCase();
    if (/\bcash\b/.test(t)) return 'cash';
    if (/visa|master ?card|amex|american express|discover|jcb|diners|credit|debit|\bcard\b/.test(t)) return 'credit';
    return 'alternative';
}

/**
 * REF-4 — the channel grouping the Sales by Channel breakdown buckets rows into. Stored on
 * the refund record rather than inferred from the channel label at read time, so the
 * breakdown and the refund agree by construction instead of by a regex that can drift.
 */
export type RefundChannelGroup = 'DIRECT_ONLINE' | 'IN_STORE' | 'MARKETPLACE';

/**
 * One refund event, keyed to its order. REF-1 notes there is at most ONE per order for
 * this release, but the vector is stored as a discrete record rather than folded onto the
 * order so a refund event log remains possible without a data migration.
 */
export interface RefundRecord {
    id: string;
    orderId: string;
    orderNo: string;
    /** The ORIGINAL order date. */
    orderDate: string;
    /**
     * REF-4 — the date the refund posted, which may differ from `orderDate`. The Taxes and
     * Funds tables post on THIS date, so they can legitimately show a refund with no
     * same-day sale.
     */
    refundedAt: string;
    type: RefundType;
    components: RefundComponents;
    reason: string;
    reasonGroup: RefundReasonGroup;
    /** Who processed the refund — drives Refunds by Staff. */
    staffName: string;
    /** REF-6 — tips and gratuity are attributed to the CHECK OWNER, not the refunder. */
    checkOwner: string;
    tenders: RefundTenderReturn[];
    channel: string;
    /** REF-4 — which Sales-by-Channel group this order's refund belongs to. */
    channelGroup: RefundChannelGroup;
    /** REF-4 — the fulfillment sub-row label: 'Delivery' | 'Pickup' | 'Dine-In' | 'In-Store'. */
    fulfillment: string;
    /** REF-7 — the guest, so the Customers report can absorb ITEM refunds into net spend. */
    customerName: string | null;
    /** REF-1 — fee refunds are Direct only. */
    salesSource: 'direct' | 'thirdparty';
    /**
     * REF-6 — the tip period had already closed (tip-out paid) when this refund posted.
     * Surfaces the exposure on the Staff Tips row rather than blocking the refund.
     */
    refundedAfterTipOut?: boolean;
    /**
     * Order-detail drawer only — one entry per distinct refund action, in API order (never
     * deduped). Absent/empty → the drawer falls back to the record's own single event.
     */
    details?: RefundDetail[];
    createdAt: string;
    updatedAt: string;
}

/** One row group in the drawer's Refund History (when · who · type · why). */
export interface RefundDetail {
    /** Raw store-local text, shown as-is when it can't be parsed. */
    refundedAt: string;
    /** ISO-ish ('YYYY-MM-DDTHH:mm:ss') form of `refundedAt` for the wall-clock formatter. */
    createdAt: string;
    staffName: string;
    type: RefundType;
    reason: string;
}

const n = (v: number | null | undefined): number => (Number.isFinite(Number(v)) ? Number(v) : 0);
const r2 = (v: number): number => Number(v.toFixed(2));

export const emptyComponents = (): RefundComponents =>
    ({ item: 0, tax: 0, tip: 0, gratuity: 0, convenienceFee: 0, deliveryFee: 0, serviceCharge: 0, cardProcessingFee: 0 });

/** Total cash returned across ALL components — the REF-3 KPI definition. */
export const refundTotal = (c: RefundComponents): number =>
    r2(n(c.item) + n(c.tax) + n(c.tip) + n(c.gratuity) + n(c.convenienceFee) + n(c.deliveryFee) + n(c.serviceCharge) + n(c.cardProcessingFee));

/**
 * REF-7 — THE revenue accessor. Any report computing item, category, guest or loyalty
 * revenue subscribes to this and never to `refundTotal`.
 */
export const itemRefundAmount = (c: RefundComponents): number => r2(n(c.item));

export const taxRefundAmount = (c: RefundComponents): number => r2(n(c.tax));

export const tipsAndGratuityRefundAmount = (c: RefundComponents): number =>
    r2(n(c.tip) + n(c.gratuity));

/** Convenience + delivery + service charge + card processing fee — all post to the
 * waterfall's `Others` row. */
export const feeRefundAmount = (c: RefundComponents): number =>
    r2(n(c.convenienceFee) + n(c.deliveryFee) + n(c.serviceCharge) + n(c.cardProcessingFee));

/** Everything that is NOT an item refund. Non-zero here is why the KPI differs from the row. */
export const nonItemRefundAmount = (c: RefundComponents): number =>
    r2(refundTotal(c) - itemRefundAmount(c));

export const addComponents = (a: RefundComponents, b: RefundComponents): RefundComponents => ({
    item: r2(n(a.item) + n(b.item)),
    tax: r2(n(a.tax) + n(b.tax)),
    tip: r2(n(a.tip) + n(b.tip)),
    gratuity: r2(n(a.gratuity) + n(b.gratuity)),
    convenienceFee: r2(n(a.convenienceFee) + n(b.convenienceFee)),
    deliveryFee: r2(n(a.deliveryFee) + n(b.deliveryFee)),
    serviceCharge: r2(n(a.serviceCharge) + n(b.serviceCharge)),
    cardProcessingFee: r2(n(a.cardProcessingFee) + n(b.cardProcessingFee)),
});

export const sumComponents = (records: RefundRecord[]): RefundComponents =>
    records.reduce((acc, rec) => addComponents(acc, rec.components), emptyComponents());

/**
 * REF-1 — derive the refund type from the vector rather than trusting a stored flag, so a
 * record can never claim 'fee' while carrying an item amount.
 *
 * `full` is not derivable from the vector alone (it depends on whether the whole order was
 * returned), so it is passed through when the source asserts it.
 */
export function deriveRefundType(c: RefundComponents, asserted?: RefundType): RefundType {
    if (asserted === 'full') return 'full';
    return itemRefundAmount(c) > 0 ? 'item' : 'fee';
}

/** REF-5 — the order status badge. */
export function refundStatusLabel(type: RefundType): string {
    return type === 'full' ? 'Refunded' : 'Partially Refunded';
}

/** REF-5 edge case — the per-tender returns must sum to the refund total. */
export function tendersFoot(rec: RefundRecord): boolean {
    const returned = rec.tenders.reduce((s, t) => s + n(t.amount), 0);
    return Math.abs(r2(returned) - refundTotal(rec.components)) <= 0.01;
}

/* ─── Period aggregate ──────────────────────────────────────────────────────── */

export interface StaffRefundRow {
    staffName: string;
    itemCount: number;
    feeCount: number;
    /** Ranking is by TOTAL amount (REF-6), not by count. */
    total: number;
    itemAmount: number;
    feeAmount: number;
}

export interface ReasonRow {
    reason: string;
    group: RefundReasonGroup;
    count: number;
    amount: number;
}

export interface RefundPeriodSummary {
    records: RefundRecord[];
    orderCount: number;
    components: RefundComponents;
    /** Total cash returned — the REF-3 KPI value. */
    total: number;
    /** The only part that reduces Net Sales — the waterfall's `Refunds` row. */
    itemTotal: number;
    taxTotal: number;
    tipsAndGratuityTotal: number;
    /** Convenience + delivery, posting to `Others`. */
    otherFeesTotal: number;
    /** True when any non-item component was returned — drives the waterfall sub-labels. */
    hasFeeRefunds: boolean;
    reasons: ReasonRow[];
    staff: StaffRefundRow[];
    /** REF-6 — staff with a tip refunded after their tip period closed. */
    tipOutExposure: Record<string, number>;
}

export function summarizeRefunds(records: RefundRecord[]): RefundPeriodSummary {
    const components = sumComponents(records);

    const reasonMap = new Map<string, ReasonRow>();
    const staffMap = new Map<string, StaffRefundRow>();
    const tipOutExposure: Record<string, number> = {};

    for (const rec of records) {
        const total = refundTotal(rec.components);
        const item = itemRefundAmount(rec.components);
        const fee = r2(total - item);

        const rKey = `${rec.reasonGroup}:${rec.reason}`;
        const reason = reasonMap.get(rKey) ?? { reason: rec.reason, group: rec.reasonGroup, count: 0, amount: 0 };
        reason.count += 1;
        reason.amount = r2(reason.amount + total);
        reasonMap.set(rKey, reason);

        const s = staffMap.get(rec.staffName) ?? {
            staffName: rec.staffName, itemCount: 0, feeCount: 0, total: 0, itemAmount: 0, feeAmount: 0,
        };
        // REF-6 — the two counts are separated, never summed: a fee-refund cluster on one
        // staff member is a control signal, an item-refund cluster is a quality signal.
        if (item > 0) s.itemCount += 1; else s.feeCount += 1;
        s.itemAmount = r2(s.itemAmount + item);
        s.feeAmount = r2(s.feeAmount + fee);
        s.total = r2(s.total + total);
        staffMap.set(rec.staffName, s);

        if (rec.refundedAfterTipOut) {
            const exposed = tipsAndGratuityRefundAmount(rec.components);
            if (exposed > 0) {
                tipOutExposure[rec.checkOwner] = r2((tipOutExposure[rec.checkOwner] ?? 0) + exposed);
            }
        }
    }

    const itemTotal = itemRefundAmount(components);
    const taxTotal = taxRefundAmount(components);
    const tipsAndGratuityTotal = tipsAndGratuityRefundAmount(components);
    const otherFeesTotal = feeRefundAmount(components);

    return {
        records,
        orderCount: records.length,
        components,
        total: refundTotal(components),
        itemTotal,
        taxTotal,
        tipsAndGratuityTotal,
        otherFeesTotal,
        hasFeeRefunds: taxTotal + tipsAndGratuityTotal + otherFeesTotal > 0,
        reasons: [...reasonMap.values()].sort((a, b) => b.count - a.count || b.amount - a.amount),
        staff: [...staffMap.values()].sort((a, b) => b.total - a.total),
        tipOutExposure,
    };
}

/**
 * REF-4 — refunded tax per DATE, keyed on `refundedAt`.
 *
 * "A refund posts on the date the refund occurred, not the original order date — the Taxes
 * and Funds tables may show a refund with no corresponding same-day sale." Keying on
 * `orderDate` here would quietly re-date the refund onto a day it did not affect, which is
 * exactly the reconciliation error the column exists to prevent.
 */
export function taxRefundsByDate(records: RefundRecord[]): Record<string, number> {
    const out: Record<string, number> = {};
    for (const rec of records) {
        const amt = taxRefundAmount(rec.components);
        if (amt <= 0) continue;
        out[rec.refundedAt] = r2((out[rec.refundedAt] ?? 0) + amt);
    }
    return out;
}

/** REF-4 Funds — total cash returned per date, for the Processed Funds deduction. */
export function refundTotalsByDate(records: RefundRecord[]): Record<string, number> {
    const out: Record<string, number> = {};
    for (const rec of records) {
        const amt = refundTotal(rec.components);
        if (amt <= 0) continue;
        out[rec.refundedAt] = r2((out[rec.refundedAt] ?? 0) + amt);
    }
    return out;
}

/* ─── REF-4 tender attribution (EOD Summary Report) ─────────────────────────── */

export interface TenderClassTotals { cash: number; credit: number; alternative: number }

export const emptyTenderTotals = (): TenderClassTotals => ({ cash: 0, credit: 0, alternative: 0 });

/**
 * REF-4 — "Cash Sales, Alternative Payments and Credit Sales are net of refunds by tender,
 * since each component returns to its originating tender."
 *
 * Split by the PER-TENDER return amounts, not by the refund total: an order settled across
 * a card and cash returns proportionally to both, and charging the whole refund to one
 * bucket would leave the other overstated by exactly the same amount.
 */
export function refundsByTenderClass(records: RefundRecord[]): TenderClassTotals {
    const out = emptyTenderTotals();
    for (const rec of records) {
        for (const t of rec.tenders) {
            out[tenderClassOf(t.tender)] = r2(out[tenderClassOf(t.tender)] + n(t.amount));
        }
    }
    return out;
}

/** Same split, keyed on the date the refund POSTED (REF-4), for a per-day table. */
export function refundsByTenderClassByDate(records: RefundRecord[]): Record<string, TenderClassTotals> {
    const out: Record<string, TenderClassTotals> = {};
    for (const rec of records) {
        const day = out[rec.refundedAt] ?? (out[rec.refundedAt] = emptyTenderTotals());
        for (const t of rec.tenders) {
            const cls = tenderClassOf(t.tender);
            day[cls] = r2(day[cls] + n(t.amount));
        }
    }
    return out;
}

/* ─── REF-4 channel attribution (Sales by Channel — Financial Breakdown) ─────── */

export interface ChannelRefunds {
    components: RefundComponents;
    /** Order-level total — what the new `Refunded` column shows. */
    total: number;
    /** Keyed by fulfillment label, for the expandable sub-rows. */
    byFulfillment: Record<string, { components: RefundComponents; total: number }>;
}

/**
 * REF-4 — refunds bucketed the way the Sales by Channel breakdown buckets its rows, so each
 * money column can be shown net of ITS OWN refunds rather than of the order total.
 */
export function refundsByChannelGroup(records: RefundRecord[]): Record<string, ChannelRefunds> {
    const out: Record<string, ChannelRefunds> = {};
    for (const rec of records) {
        const g = out[rec.channelGroup] ?? (out[rec.channelGroup] = {
            components: emptyComponents(), total: 0, byFulfillment: {},
        });
        g.components = addComponents(g.components, rec.components);
        g.total = r2(g.total + refundTotal(rec.components));

        const f = g.byFulfillment[rec.fulfillment] ?? (g.byFulfillment[rec.fulfillment] = {
            components: emptyComponents(), total: 0,
        });
        f.components = addComponents(f.components, rec.components);
        f.total = r2(f.total + refundTotal(rec.components));
    }
    return out;
}

/* ─── REF-6 staff attribution ───────────────────────────────────────────────── */

export interface StaffTipRefund {
    tips: number;
    gratuity: number;
    total: number;
    /** REF-6 — part of `total` that was refunded AFTER the tip period closed. */
    afterTipOut: number;
}

/**
 * REF-6 — "Tip and gratuity refunds are attributed to the CHECK OWNER."
 *
 * Not to the staff member who pressed refund: the tip belonged to whoever owned the check,
 * so that is whose tip total must fall. Attributing it to the refunder would penalise a
 * manager for processing someone else's correction.
 */
export function tipRefundsByCheckOwner(records: RefundRecord[]): Record<string, StaffTipRefund> {
    const out: Record<string, StaffTipRefund> = {};
    for (const rec of records) {
        const amount = tipsAndGratuityRefundAmount(rec.components);
        if (amount <= 0) continue;
        const row = out[rec.checkOwner] ?? (out[rec.checkOwner] = { tips: 0, gratuity: 0, total: 0, afterTipOut: 0 });
        row.tips = r2(row.tips + n(rec.components.tip));
        row.gratuity = r2(row.gratuity + n(rec.components.gratuity));
        row.total = r2(row.total + amount);
        if (rec.refundedAfterTipOut) row.afterTipOut = r2(row.afterTipOut + amount);
    }
    return out;
}

/**
 * REF-6 — "Item refunds reduce Staff Sales Leaderboard revenue. Fee refunds do not."
 *
 * Attributed to the CHECK OWNER for the same reason as tips: the leaderboard ranks who SOLD
 * the food, so the sale that came back must be removed from the seller, not from whoever
 * processed the return.
 */
export function itemRefundsByCheckOwner(records: RefundRecord[]): Record<string, number> {
    const out: Record<string, number> = {};
    for (const rec of records) {
        const amount = itemRefundAmount(rec.components);
        if (amount <= 0) continue;
        out[rec.checkOwner] = r2((out[rec.checkOwner] ?? 0) + amount);
    }
    return out;
}

/* ─── REF-7 containment ─────────────────────────────────────────────────────── */

export interface CustomerRefunds {
    /** REF-7 — the ONLY figure allowed to reduce CLV and Net Spend. */
    itemRefunds: number;
    /** All components. Shown in the Order Log's Refunded column; never netted into CLV. */
    total: number;
}

/**
 * REF-7 — "CLV and Net Spend absorb item refunds only. Fee refunds appear in the Order Log
 * Refunded column but do not reduce CLV."
 *
 * Both figures are returned together deliberately: a caller that only ever sees the total
 * will eventually subtract it from spend, so the accessor hands over the correct figure for
 * revenue alongside the one for display and names which is which.
 */
export function refundsByCustomer(records: RefundRecord[]): Record<string, CustomerRefunds> {
    const out: Record<string, CustomerRefunds> = {};
    for (const rec of records) {
        if (!rec.customerName) continue;
        const row = out[rec.customerName] ?? (out[rec.customerName] = { itemRefunds: 0, total: 0 });
        row.itemRefunds = r2(row.itemRefunds + itemRefundAmount(rec.components));
        row.total = r2(row.total + refundTotal(rec.components));
    }
    return out;
}

/**
 * REF-7 — loyalty and offers reversal rule.
 *
 * "A fee refund does NOT reverse points or restore a redemption discount. An item or full
 * refund continues to reverse both per existing rules." Exposed as a predicate so a report
 * asks the question rather than re-deriving it from a component vector it half-understands.
 */
export const reversesLoyalty = (rec: RefundRecord): boolean => itemRefundAmount(rec.components) > 0;

/* ─── REF-1 waterfall wiring ────────────────────────────────────────────────── */

/**
 * What the waterfall needs to know about refunds in the period.
 *
 * `SalesWaterfall` takes this instead of a bare number so the three cash-reconciliation
 * rows can render their "net of $X refunded" sub-label, and so the `Refunds` row can be
 * fed the ITEM component while the REFUND KPI above it shows the total. The two figures
 * differing is correct and intentional (REF-1); REF-3 handles the labelling.
 */
export interface WaterfallRefundContext {
    /** Posts to the `Refunds` row. */
    itemRefunds: number;
    /** Netted out of `Tax collected`. */
    taxRefunds: number;
    /** Netted out of `Tips & Gratuity`. */
    tipsAndGratuityRefunds: number;
    /** Netted out of `Others`. */
    otherFeeRefunds: number;
    /** Total cash returned — what the REFUND KPI reads. */
    totalRefunds: number;
}

export const toWaterfallRefundContext = (s: RefundPeriodSummary): WaterfallRefundContext => ({
    itemRefunds: s.itemTotal,
    taxRefunds: s.taxTotal,
    tipsAndGratuityRefunds: s.tipsAndGratuityTotal,
    otherFeeRefunds: s.otherFeesTotal,
    totalRefunds: s.total,
});

/**
 * REF-1 — real per-period refund figures, from GET /sales/live/v2/snapshot-details's
 * `refund` (item) / `feeRefund` (fee) fields — the same figures the Refunds stamp KPI
 * shows, so the waterfall's Refunds/Others rows can never disagree with that card.
 * `itemRefund` posts to the Refunds row (reduces Net Sales), `feeRefund` nets out of
 * Others. Tax and tips/gratuity refund components are not split out by this endpoint yet,
 * so those two rows stay un-netted (0) until it grows richer.
 */
export const waterfallRefundContextFromSnapshot = (itemRefund: number | null | undefined, feeRefund: number | null | undefined): WaterfallRefundContext => {
    const item = r2(n(itemRefund));
    const fee = r2(n(feeRefund));
    return {
        itemRefunds: item,
        taxRefunds: 0,
        tipsAndGratuityRefunds: 0,
        otherFeeRefunds: fee,
        totalRefunds: r2(item + fee),
    };
};

/* ─── REF-3 KPI netting ─────────────────────────────────────────────────────── */

/**
 * REF-3 — "adjacent KPIs become net of their own refunds". Each of these takes the GROSS
 * KPI value and subtracts only the components that belong to it.
 *
 * A staff member's net tip total is allowed to go NEGATIVE in a short period (REF-6): the
 * value is returned as-is and never floored at zero.
 */
export const netOfTaxRefunds = (grossTax: number, s: RefundPeriodSummary): number =>
    r2(n(grossTax) - s.taxTotal);

export const netOfTipRefunds = (grossTips: number, s: RefundPeriodSummary): number =>
    r2(n(grossTips) - s.tipsAndGratuityTotal);

/**
 * Convenience + delivery + card processing fee refunds — `otherFeesTotal` minus service
 * charge. Outlets with Service Charge enabled report it as its own stamp KPI (and its own
 * waterfall line, never folded into "Other"/"Others"), so netting the "Other" KPI by the
 * full fee bucket (which now includes service charge, per REF-5's 8-component model) would
 * double-subtract a service-charge refund out of a tile it was never added into.
 * `otherFeesTotal` itself is left untouched — the Refund Log's own Component Split still
 * wants the full all-fee bucket, service charge included.
 */
export const nonServiceChargeFeeRefunds = (s: RefundPeriodSummary): number =>
    r2(s.otherFeesTotal - n(s.components.serviceCharge));

export const netOfFeeRefunds = (grossOther: number, s: RefundPeriodSummary): number =>
    r2(n(grossOther) - nonServiceChargeFeeRefunds(s));

export const netOfAllRefunds = (grossCollected: number, s: RefundPeriodSummary): number =>
    r2(n(grossCollected) - s.total);

/**
 * REF-3 — the SNAP-MD refund-rate sub-line. The numerator is ITEM REFUNDS ONLY so a
 * tax-only refund cannot inflate the ratio or trip the orange/red thresholds; the
 * denominator stays Net Sales, keeping it a comparable revenue-loss signal.
 */
export function refundRatePct(s: RefundPeriodSummary, netSales: number): number | null {
    const base = n(netSales);
    if (base <= 0) return null;
    return Number(((s.itemTotal / base) * 100).toFixed(2));
}

/** REF-3 — one definition of the KPI tooltip, used on all three reports. */
export const REFUND_KPI_TOOLTIP =
    'Total returned to guests, including any refunded tax, tips, gratuity and fees. Only item refunds reduce Net Sales.';
